import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Chip, Avatar, Tooltip } from "@mui/material";
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { MdOutlineEdit, MdOutlineMotionPhotosPaused } from "react-icons/md";
import { useSelector } from 'react-redux';
import { UserAPI } from "../../../api/UserAPI";
import { SubscriptionAPI } from "../../../api/SubscriptionAPI";
import defaultProfileImg from '../../../../src/assets/images/default_profile_img.png';
import freeTierImg from '../../../../src/assets/images/free-trial.png';
import { GrInProgress } from "react-icons/gr";
import { Privileges } from "../../../util/PrivilegeConstants";
import { useDispatch } from "react-redux";
import { PrivilegeAPI } from "../../../api/PrivilegesAPI";
import { rootAction } from "../../../redux/reducer/rootReducer";
import CustomDialog from '../../CustomComponents/CustomDialog/CustomDialog.jsx';
import { getPlayerLabelByGame } from "../../../util/SideBarUtil.jsx";
import { canChangeClubRole, formatRoleLabel, primaryClubRole, ROLE_API_NAMES, toManagePlayerRow } from "../../../util/UserRoles";

// ✅ Custom Toolbar with built-in quick filter
const CustomToolbar = ({ quickFilterProps }) => {
    return (
        <div style={{ 
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center',
            }}>
                <GridToolbarQuickFilter
                    {...quickFilterProps}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            height: '38px',
                            backgroundColor: 'white',
                            width: '320px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            padding: '0 12px',
                        },
                    }}
                    placeholder="Search by Name, Email, Phone, or Role..."
                    debounceMs={400}
                    quickFilterParser={(searchInput) => 
                        searchInput.split(',').map((value) => value.trim())
                    }
                />
            </div>
        </div>
    );
};

const PlayerList = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const dispatch = useDispatch();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [roleEdit, setRoleEdit] = useState({ userId: null, role: ROLE_API_NAMES.PLAYER });
    const [loadingState, setLoadingState] = useState({
        loading1: false,
        loading2: false,
        loading3: false
    })
    const [users, setUsers] = useState([]);
    const [cancelSubInfo, setCancelSubInfo] = useState(null);
    const [refresh, setRefresh] = useState('');
    const [isRefundAll, setIsRefundAll] = useState(false);
    const [privileges, setPrivileges] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [filterData, setFilterData] = useState({});
    const [filterModel, setFilterModel] = useState({ items: [] });
    
    // ✅ Track sorting state with default sort by playerName
    const [sortModel, setSortModel] = useState([
        { field: 'playerName', sort: 'asc' }
    ]);

    const startLoading = (key) => {
        setLoadingState(prev => ({
            ...prev, [key]: true
        }))
    };
    const finishLoading = (key) => {
        setLoadingState(prev => ({
            ...prev, [key]: false
        }))
    };

    const findAllPrivileges = async () => {
        try {
            startLoading("loading1")
            const [planResponse, inviteResponse] = await Promise.all([
                PrivilegeAPI.getAllPrivilegesByFeature({
                    userId: userInfo.userId,
                    featureName: Object.keys(Privileges)[7]
                }),
                PrivilegeAPI.getAllPrivilegesByFeature({
                    userId: userInfo.userId,
                    featureName: Object.keys(Privileges)[10]
                }),
            ]);
            setPrivileges({ ...planResponse.Data, ...inviteResponse.Data });
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading("loading1")
        }
    };

    const handleOpenRoleDialog = (row) => {
        setRoleEdit({
            userId: row.playerId,
            role: primaryClubRole(row.roles, row.roleName),
        });
        setOpenRoleDialog(true);
    };

    const handleCloseRoleDialog = () => {
        setOpenRoleDialog(false);
        setRoleEdit({ userId: null, role: ROLE_API_NAMES.PLAYER });
    };

    const handleSaveRole = async () => {
        try {
            startLoading("loading3");
            await UserAPI.updateUserRole(roleEdit.userId, { roleNames: [roleEdit.role] });
            setUsers(prev => prev.map(item =>
                item.playerId === roleEdit.userId
                    ? {
                        ...item,
                        roleName: roleEdit.role === ROLE_API_NAMES.PLAYER ? 'player' : roleEdit.role.toLowerCase(),
                        roles: [roleEdit.role === ROLE_API_NAMES.PLAYER ? 'user' : roleEdit.role.toLowerCase()],
                    }
                    : item
            ));
            handleCloseRoleDialog();
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: 'Role updated successfully' }));
            setRefresh(Date.now());
        } catch (error) {
            console.error(error);
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: error?.response?.data?.Data || 'Please contact customer support' }));
        } finally {
            finishLoading("loading3");
        }
    };

    // ✅ Handle API call with filter and sorting
    const fetchMemberAndPlayers = async (filterText = '', size, pageNum, sortData = null) => {
        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;
            
            // ✅ Build sort params from sortModel
            let sortParams = {};
            if (sortData) {
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'playerName': 'playerName',
                    'roleName': 'roleName',
                    'playerStatus': 'playerStatus',
                    'email': 'email',
                    'subStatus': 'subStatus',
                    'planName': 'planName'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
        //     console.log("🔍 Calling API with:", { 
        //         page: finalPage, 
        //         size: finalSize, 
        //         filter: filterText,
        //         sort: sortParams 
        //     }
        
        // );
            
            startLoading("loading2")
            const res = await UserAPI.getAllMembersForAClub(
                selectedClub.clubId,
                finalPage,
                finalSize,
                filterText || '',
                filterData,
            )
            setUsers((res.Data || []).map(toManagePlayerRow));
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading("loading2")
        }
    }

    // ✅ Handle sort model changes
    const handleSortModelChange = (newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Build sort params
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'playerName': 'playerName',
                'roleName': 'roleName',
                'playerStatus': 'playerStatus',
                'email': 'email',
                'subStatus': 'subStatus',
                'planName': 'planName'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchMemberAndPlayers(filterValue, paginationModel.pageSize, 0, sortParams);
    };

    // ✅ Handle filter changes from DataGrid
    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel);
        
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Preserve current sort when filtering
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'playerName': 'playerName',
                'roleName': 'roleName',
                'playerStatus': 'playerStatus',
                'email': 'email',
                'subStatus': 'subStatus',
                'planName': 'planName'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchMemberAndPlayers(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllPrivileges();
        }
    }, [userInfo.userId])

    // ✅ Initial load and pagination changes with sorting
    useEffect(() => {
        if (selectedClub?.clubId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'playerName': 'playerName',
                    'roleName': 'roleName',
                    'playerStatus': 'playerStatus',
                    'email': 'email',
                    'subStatus': 'subStatus',
                    'planName': 'planName'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            fetchMemberAndPlayers(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [selectedClub?.clubId, paginationModel.page, paginationModel.pageSize, refresh]);
    
    const cancelSubscription = (subscriptionId, userId, userName) => {
        setOpenDeleteModal(true);
        setCancelSubInfo({
            userId: userId,
            clubId: selectedClub.clubId,
            subscriptionId: subscriptionId,
            subscriptionCancelReason: "N/A",
            userName: userName
        })
    }
    
    const cancelSubscriptionApi = async () => {
        try {
            setOpenDeleteModal(false);
            startLoading("loading3")
            const result = await SubscriptionAPI.deleteSubscription(cancelSubInfo, isRefundAll)
            setRefresh(true);
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: cancelSubInfo.userName + ' subscription is canceled successfully' }));
        } catch (error) {
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: 'Something went wrong' }));
        } finally {
            finishLoading("loading3")
        }
    }

    // ✅ Updated columns with proper widths, sorting, and filter disabled
    const columns = useMemo(() => [
        {
            field: 'profileImage',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 70,
            maxWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.row.playerName || ''} arrow>
                    <Avatar 
                        src={params.value ? `${import.meta.env.VITE_APP_BASE_URL}/user/user-profile-img/${params.value}` : defaultProfileImg}
                        sx={{ width: 40, height: 40 }}
                    />
                </Tooltip>
            )
        },
        {
            field: 'playerName',
            headerName: 'Name',
            flex: 1.2,
            minWidth: 150,
            maxWidth: 250,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'roleName',
            headerName: 'Role',
            flex: 0.8,
            minWidth: 140,
            maxWidth: 200,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const canEditRole = privileges?.MANAGE_INVITE_STAFF?.includes(Privileges.MODIFY)
                    && canChangeClubRole(params.row.roles || [params.value]);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Chip label={formatRoleLabel(params.value)} size="small" />
                        {canEditRole && (
                            <Tooltip title="Change role" arrow>
                                <MdOutlineEdit
                                    data-testid="change-role"
                                    style={{ cursor: 'pointer', fontSize: '18px' }}
                                    onClick={() => handleOpenRoleDialog(params.row)}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'playerStatus',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                return (
                    <Chip
                        icon={val === 'ACTIVE' ? <FaCheckCircle /> : <GrInProgress />}
                        label={val === 'ACTIVE' ? 'Active' : 'Pending'}
                        color={val === 'ACTIVE' ? 'success' : 'warning'}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || 'N/A',
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'phone',
            headerName: 'Phone',
            flex: 0.9,
            minWidth: 140,
            maxWidth: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'subStatus',
            headerName: 'Plan Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                if (!val) return <span>No plan</span>;
                
                let icon = null;
                let label = val;
                let color = 'default';
                
                if (val === 'active') {
                    icon = <FaCheckCircle style={{ color: '#2e7d32', marginRight: '4px' }} />;
                    label = 'Active';
                    color = 'success';
                } else if (val === 'paused') {
                    icon = <MdOutlineMotionPhotosPaused style={{ color: '#ed6c02', marginRight: '4px' }} />;
                    label = 'Paused';
                    color = 'warning';
                } else if (val === 'trialing') {
                    icon = <img src={freeTierImg} style={{ height: '20px', marginRight: '4px' }} alt="free" />;
                    label = 'Free Tier';
                    color = 'info';
                } else if (val === 'canceled') {
                    icon = <IoIosCloseCircle style={{ color: '#d32f2f', marginRight: '4px' }} />;
                    label = 'Canceled';
                    color = 'error';
                }
                
                return (
                    <Chip
                        icon={icon}
                        label={label}
                        color={color}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'planName',
            headerName: 'Plan Name',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => {
                if (!params?.value) return 'No plan';
                return params.value.replaceAll('-', ' ');
            }
        },
        {
            field: 'actions',
            headerName: 'Cancel Plan?',
            flex: 0.6,
            minWidth: 120,
            maxWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                const canCancel = privileges?.MANAGE_CANCEL_USER_PLAN?.includes(Privileges.CANCEL);
                const showCancel = row.subStatus === 'active' && row.planName !== 'pay-as-you-go';
                
                if (!showCancel || !canCancel) return null;
                
                return (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => {
                            const userName = row.playerName || '';
                            cancelSubscription(row.subId, row.playerId, userName);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                );
            }
        },
    ], [privileges]);

    const rowsWithId = users.map((row, index) => ({
        ...row,
        id: row.playerId || row.id || `row-${index}`
    }));

    // Check if any loading state is true
    const isLoading = loadingState.loading1 || loadingState.loading2 || loadingState.loading3;

    return (
        <div className="member-list-container">
            <div className="add-member-heading">
                <h1>Manage {getPlayerLabelByGame(selectedClub?.games && selectedClub?.games[0]?.gameName)}</h1>
            </div>
            <div className="member-list-table">
                <div style={{ height: 850, width: '100%', background: 'white', borderRadius: '8px', padding: '16px' }}>
                    <DataGrid
                        rows={rowsWithId}
                        columns={columns}
                        pagination
                        paginationMode="server"
                        rowCount={users?.length !== 0 ? users[0]?.totalPlayer : 0}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => {
                            setPaginationModel(newModel);
                        }}
                        pageSizeOptions={[50, 100]}
                        loading={isLoading}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                        
                        // ✅ Sorting
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        
                        // ✅ Filter handling
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterChange}
                        
                        // ✅ Ensure column resizing is enabled
                        disableColumnResize={false}
                        
                        slots={{
                            toolbar: () => (
                                <CustomToolbar
                                    quickFilterProps={{
                                        debounceMs: 400,
                                    }}
                                />
                            ),
                        }}
                        hideFooterPagination={false}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e8e8e8',
                                fontWeight: 600,
                                color: '#333',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                            },
                            // ✅ Clean column separator - let MUI handle resizing
                            '& .MuiDataGrid-columnSeparator': {
                                visibility: 'visible',
                                color: '#d1d1d1',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                padding: '8px 16px',
                                fontSize: '14px',
                                color: '#333',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f9ff',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e8e8e8',
                                padding: '8px 16px',
                                backgroundColor: '#fafafa',
                            },
                            '& .MuiDataGrid-toolbarContainer': {
                                padding: 0,
                            },
                            border: 'none',
                            borderRadius: '8px',
                            // ✅ Hide column menu icon
                            '& .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </div>
            </div>
            <CustomDialog
                title="Change role"
                isOpen={openRoleDialog}
                submitBtnName="Save"
                onSubmit={handleSaveRole}
                onCancel={handleCloseRoleDialog}
                onClose={handleCloseRoleDialog}
            >
                <FormControl fullWidth>
                    <InputLabel id="manage-player-role-label">Role</InputLabel>
                    <Select
                        labelId="manage-player-role-label"
                        label="Role"
                        value={roleEdit.role}
                        onChange={(event) => setRoleEdit(prev => ({ ...prev, role: event.target.value }))}
                    >
                        <MenuItem value={ROLE_API_NAMES.PLAYER}>Player</MenuItem>
                        <MenuItem value={ROLE_API_NAMES.STAFF}>Staff</MenuItem>
                        <MenuItem value={ROLE_API_NAMES.COACH}>Coach</MenuItem>
                    </Select>
                </FormControl>
            </CustomDialog>
            <CustomDialog
                isOpen={openDeleteModal}
                submitBtnName="Yes"
                onSubmit={cancelSubscriptionApi}
                onCancel={() => setOpenDeleteModal(false)}
                onClose={() => setOpenDeleteModal(false)}
            >
                <FormControlLabel
                    control={<Checkbox
                        onChange={(e) => setIsRefundAll(e.target.checked)} className="reservation-calcel-checkbox"
                    />}
                    label="Refund all reserved reservation payment"
                />
            </CustomDialog>
        </div>
    )
}

export default PlayerList